'use client';

import { useEffect } from 'react';

/**
 * WebMCP (Web Model Context) integration.
 *
 * Exposes a few real, useful site actions to in-browser AI agents via the
 * experimental `navigator.modelContext` API. Everything is feature-detected,
 * so this is a no-op in browsers that don't support WebMCP and never throws.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 */

type WebMCPToolResult = {
  content: Array<{ type: 'text'; text: string }>;
};

type WebMCPTool = {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => WebMCPToolResult | Promise<WebMCPToolResult>;
};

type ModelContext = {
  provideContext?: (context: { tools: WebMCPTool[] }) => void;
  registerTool?: (tool: WebMCPTool) => void;
};

function text(value: string): WebMCPToolResult {
  return { content: [{ type: 'text', text: value }] };
}

const EMPTY_INPUT = { type: 'object', properties: {}, additionalProperties: false } as const;

function buildTools(): WebMCPTool[] {
  return [
    {
      name: 'get_company_overview',
      description:
        'Get an overview of Open (OpenCX), the unified AI customer support platform, and its core AI engine Agent 5.',
      inputSchema: EMPTY_INPUT,
      execute: () =>
        text(
          [
            'Open (OpenCX) — the unified AI customer support platform.',
            'Core engine: Agent 5 — 77% average automation rate.',
            'Channels: voice, web chat, email, SMS, WhatsApp, social, Slack.',
            'Pricing: $0.90 per automated resolution — pay only for value delivered.',
            'Docs: https://docs.open.cx · API: https://api.open.cx · Contact: mo@open.cx',
          ].join('\n'),
        ),
    },
    {
      name: 'get_pricing',
      description: 'Get Open pricing details.',
      inputSchema: EMPTY_INPUT,
      execute: () =>
        text(
          [
            'Open pricing: $0.90 per automated resolution.',
            'Pay only when the AI delivers value. No per-seat pricing, no minimums, no hidden fees.',
            'Full details: https://www.open.cx/pricing',
          ].join('\n'),
        ),
    },
    {
      name: 'list_integrations',
      description: 'List the helpdesks and tools Open integrates with.',
      inputSchema: EMPTY_INPUT,
      execute: () =>
        text(
          [
            'Open integrates with: Zendesk, Intercom, Salesforce Service Cloud, HubSpot,',
            'Freshdesk, and Twilio Flex — plus Zapier, Make, and n8n.',
            'REST API and webhooks are available for custom integrations.',
            'See https://www.open.cx/integrations',
          ].join('\n'),
        ),
    },
    {
      name: 'get_developer_resources',
      description:
        'Get developer resources for Open: REST API base URL, OpenAPI spec, authentication, and MCP server.',
      inputSchema: EMPTY_INPUT,
      execute: () =>
        text(
          [
            'REST API: https://api.open.cx (Bearer-token / JWT auth)',
            'OpenAPI spec: https://api.open.cx/api-json',
            'API reference: https://docs.open.cx/api-reference/',
            'Authentication: https://docs.open.cx/api-reference/authentication',
            'MCP server: npx @opencx/mcp — https://docs.open.cx/mcp/introduction',
            'Generate an API key: https://platform.open.cx/settings/access',
          ].join('\n'),
        ),
    },
    {
      name: 'search_documentation',
      description:
        "Search Open's documentation. Returns a link to the docs search results for the given query.",
      inputSchema: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'What to search the docs for.' },
        },
        required: ['query'],
        additionalProperties: false,
      },
      execute: (args) => {
        const query = String(args.query ?? '').trim();
        const url = `https://docs.open.cx/?q=${encodeURIComponent(query)}`;
        return text(`Open documentation search for "${query}": ${url}`);
      },
    },
    {
      name: 'book_a_demo',
      description: "Open Open's demo booking page in the browser.",
      inputSchema: EMPTY_INPUT,
      execute: () => {
        if (typeof window !== 'undefined') {
          window.location.href = '/demo';
        }
        return text('Opening the demo booking page at https://www.open.cx/demo');
      },
    },
  ];
}

export function WebMCP() {
  useEffect(() => {
    try {
      const modelContext = (
        navigator as unknown as { modelContext?: ModelContext }
      ).modelContext;
      if (!modelContext) return;

      const tools = buildTools();
      if (typeof modelContext.provideContext === 'function') {
        modelContext.provideContext({ tools });
      } else if (typeof modelContext.registerTool === 'function') {
        tools.forEach((tool) => modelContext.registerTool?.(tool));
      }
    } catch {
      // WebMCP is experimental; never let it break the page.
    }
  }, []);

  return null;
}
